/**
 * 個人スマホのスタート画面（/start）
 * 「ログインしたら、まず何をするか選ぶ」ための画面（2026-10-19 ユーザー要望）。
 * 個人アプリのホームにタイルが増えすぎて勤怠の操作が埋もれたため、
 * 大きなボタンで目的を選んでから各画面へ移動する。ポータルの会社のみ着地に使う。
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from '../components/BottomNav';
import { PushBell } from '../components/PushBell';
import { apiFetch } from '../services/api';
import { hasFeature } from '../utils/featureFlags';
import { Avatar } from './portal/ui';
import { PIconChip } from '../components/PIcon';

interface Summary { board_unread: number; chat_unread: number; manuals_unread: number; my_requests_pending: number; my_expenses_active: number }

const ACCENT = '#0f766e';

export default function PersonalStartPage() {
  const nav = useNavigate();
  const [sum, setSum] = useState<Summary | null>(null);
  const [dms, setDms] = useState(0);
  const [compBalance, setCompBalance] = useState<number | null>(null);
  const user = (() => { try { return JSON.parse(localStorage.getItem('catms_user') ?? '{}'); } catch { return {}; } })();
  let perms: Record<string, boolean> = {};
  try { perms = JSON.parse(localStorage.getItem('catms_permissions') ?? '{}'); } catch { /* noop */ }

  useEffect(() => {
    if (hasFeature('portal')) {
      apiFetch<{ data: Summary }>('/portal/summary', {}, { noRedirect: true }).then(r => setSum(r.data)).catch(() => { /* noop */ });
      apiFetch<{ data: { balance: number } }>('/portal/comp-days/me', {}, { noRedirect: true })
        .then(r => setCompBalance(r.data?.balance ?? null)).catch(() => { /* noop */ });
    }
    apiFetch<{ data: { dms: number } }>('/messages/unread-count', {}, { noRedirect: true }).then(r => setDms(r.data?.dms ?? 0)).catch(() => { /* noop */ });
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 11 ? 'おはようございます' : hour < 18 ? 'こんにちは' : 'おつかれさまです';
  const portalOn = hasFeature('portal');
  const portalBadge = sum ? sum.board_unread + sum.chat_unread + sum.manuals_unread : 0;

  // メインの選択肢（大きい順に、よく使うもの）
  const items: Array<{ icon: string; title: string; desc: string; path: string; badge?: number }> = [
    { icon: 'clipboard', title: '勤怠を確認・修正する', desc: '出退勤の記録を見る・直す', path: '/history' },
    { icon: 'palm', title: '休暇を申請する',
      desc: compBalance !== null ? `希望休・休暇の申請（補償休日 残${Number.isInteger(compBalance) ? compBalance : compBalance.toFixed(1)}日）` : '希望休・休暇の申請',
      path: '/leave' },
    { icon: 'megaphone', title: '会社の連絡を見る', desc: portalOn ? 'お知らせ・回覧板（既読確認）' : '連絡・メッセージ', path: portalOn ? '/portal-board' : '/messages', badge: portalOn ? (sum?.board_unread ?? 0) : dms },
    ...(portalOn ? [{ icon: 'home', title: 'ポータルを開く', desc: 'チャット・マニュアル・経費・各種申請', path: '/portal-home', badge: portalBadge }] : []),
    { icon: 'alert', title: 'ヒヤリハットを報告する', desc: '気づいた危険を共有', path: '/safety-report' },
    { icon: 'user', title: 'マイページ', desc: '自分の情報・資格・緊急連絡先', path: '/portal-profile' },
  ];
  if (perms['admin_documents'] || hasFeature('extra_cert_forms')) {
    items.push({ icon: 'pen', title: '証明書・様式を作る', desc: '各種様式の作成・発行', path: '/cert-create' });
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f6f7f8' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e3e6ea', flexShrink: 0 }}>
        <div className="app-safe-band" aria-hidden="true" />
        <div style={{ padding: '4px 16px 12px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <Avatar name={user?.user_name} size={42} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: '#50545b', fontWeight: 600 }}>{greeting}</div>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#1a1c1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.user_name ?? ''} さん
            </div>
          </div>
          <PushBell />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px 90px' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1c1f', margin: '2px 2px 12px' }}>何をしますか？</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(it => (
            <button key={it.path} onClick={() => nav(it.path)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                background: '#fff', border: '1px solid #e3e6ea', borderRadius: 14,
                padding: '18px 16px', cursor: 'pointer', minHeight: 72,
              }}>
              <PIconChip name={it.icon} size={46} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 16.5, fontWeight: 800, color: '#1a1c1f', lineHeight: 1.4 }}>{it.title}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#50545b', marginTop: 2 }}>{it.desc}</span>
              </span>
              {(it.badge ?? 0) > 0 && (
                <span style={{ background: '#d93025', color: '#fff', borderRadius: 999, fontSize: 12.5, fontWeight: 800, padding: '3px 11px', flexShrink: 0 }}>
                  {it.badge}
                </span>
              )}
              <span style={{ color: '#8b9096', fontSize: 18, flexShrink: 0 }}>›</span>
            </button>
          ))}
        </div>

        <button onClick={() => nav('/home')}
          style={{
            display: 'block', width: '100%', marginTop: 14, padding: '13px 0', textAlign: 'center',
            background: 'none', border: `1.5px solid ${ACCENT}`, borderRadius: 12,
            color: '#0c5f59', fontSize: 14, fontWeight: 800, cursor: 'pointer',
          }}>
          いつものホーム画面へ（時計・その他の機能）
        </button>
      </div>

      <BottomNav />
    </div>
  );
}
